// 🚀 WICHTIG: Diag VOR allen anderen Imports laden (Polyfills aktivieren)
// 🚀 FIX: Polyfills sind auch in Telegram notwendig, aber Debug-Logs werden in diag.js deaktiviert
import './utils/diag.js';

// 🚀 Globaler Error-Handler für WalletConnect Anti-Phishing Skript
// Fängt harmlose Fehler ab, die von externen WalletConnect-Skripten kommen
if (typeof window !== 'undefined') {
  const originalOnError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    // Ignoriere WalletConnect Anti-Phishing Fehler (nicht kritisch)
    if (
      typeof message === 'string' && 
      (message.includes('antiPhishingManagerCS') || 
       message.includes('Cannot read properties of null') ||
       (source && source.includes('antiPhishingManagerCS')))
    ) {
            return true; // Fehler als behandelt markieren
    }
    
    // Alle anderen Fehler normal behandeln
    if (typeof originalOnError === 'function') {
      return originalOnError.call(this, message, source, lineno, colno, error);
    }
    return false;
  };
  
  // Auch unhandled promise rejections abfangen
  window.addEventListener('unhandledrejection', function(event) {
    const reason = event.reason;
    const message = reason?.message || String(reason);
    
    if (
      typeof message === 'string' && 
      (message.includes('antiPhishingManagerCS') || 
       message.includes('Cannot read properties of null'))
    ) {
            event.preventDefault(); // Verhindere dass der Fehler in der Konsole erscheint
    }
  });
  
  // 🚀 FIX: Telegram WebView - "Error invoking post: Method not found" abfangen
  const isTelegram = /Telegram/i.test(navigator.userAgent || '');
  if (isTelegram) {
    const originalOnError = window.onerror;
    window.onerror = function(message, source, lineno, colno, error) {
      // Abfangen von "Error invoking post" Fehlern in Telegram
      if (
        typeof message === 'string' && 
        (message.includes('Error invoking post') || 
         message.includes('Method not found') ||
         message.includes('post is not a function'))
      ) {
                return true; // Fehler als behandelt markieren
      }
      
      // Alle anderen Fehler normal behandeln
      if (typeof originalOnError === 'function') {
        return originalOnError.call(this, message, source, lineno, colno, error);
      }
      return false;
    };
    
    // Auch Promise Rejections für post() Fehler abfangen
    window.addEventListener('unhandledrejection', function(event) {
      const reason = event.reason;
      const message = reason?.message || String(reason);
      
      if (
        typeof message === 'string' && 
        (message.includes('Error invoking post') || 
         message.includes('Method not found') ||
         message.includes('post is not a function'))
      ) {
                event.preventDefault();
      }
    });
  }
}

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { DAppKitProvider } from '@vechain/dapp-kit-react';
import './index.css';

import AppShell from './AppShell.jsx';
import AnalysisApp from './AnalysisApp.jsx';
import PaymentSuccess from './components/PaymentSuccess.jsx';
import PaymentCancelled from './components/PaymentCancelled.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import { LanguageProvider } from './contexts/LanguageContext.jsx';
import { AuthProvider } from './contexts/AuthContext.jsx';
import WalletProvider from './components/WalletProvider.jsx';
import { resetSignClient } from './utils/walletconnectClient.js';
import {
  WC_METADATA_VERSION,
  getV3TaxAbsoluteIconUrl,
  getV3TaxAppOrigin,
} from "./constants/v3taxBranding.js";

// 🚀 Test: Minimaler Provider ohne andere Wrapper

// 🚀 WICHTIG: IMMER die echte Origin verwenden (inkl. www)!
// Für Staging/VeWorld: Build mit VITE_APP_URL=https://staging.v3tax.app setzen, falls SSR/Preview ohne window.
const getDAppKitAppUrl = () => {
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
        return origin;
  }

  return import.meta.env.VITE_APP_URL ||
         (import.meta.env.PROD ? 'https://v3tax.app' : 'http://localhost:5174');
};

const appUrl = getDAppKitAppUrl();
const dappKitMetadataUrl = getV3TaxAppOrigin(appUrl);
const WALLETCONNECT_ICON_URL = getV3TaxAbsoluteIconUrl(dappKitMetadataUrl);

/** @type {'mainnet' | 'testnet'} */
const vechainNetwork = String(import.meta.env.VITE_VECHAIN_NETWORK || 'mainnet').toLowerCase() === 'testnet'
  ? 'testnet'
  : 'mainnet';

const VECHAIN_NODE_BY_NETWORK = {
  mainnet: 'https://mainnet.vechain.org/',
  testnet: 'https://testnet.vechain.org/',
};

// Explizite Override-URL möglich (z. B. Custom-Node); sonst Mainnet/Testnet wie oben.
const vechainNode =
  import.meta.env.VITE_VECHAIN_NODE_URL ||
  VECHAIN_NODE_BY_NETWORK[vechainNetwork];

const dappKitVeWorldOnly =
  import.meta.env.VITE_DAPPKIT_VEWORLD_ONLY === 'true' ||
  import.meta.env.VITE_DAPPKIT_VEWORLD_ONLY === '1';

// Für VeWorld Mobile Embedded: default false.
// Nur explizit aktivieren, wenn nach dem Connect zusätzlich eine Certificate-Signatur gewünscht ist.
const dappKitRequireCertificate =
  import.meta.env.VITE_DAPPKIT_REQUIRE_CERTIFICATE === 'true' ||
  import.meta.env.VITE_DAPPKIT_REQUIRE_CERTIFICATE === '1';

const WALLETCONNECT_APP_NAME = 'V3Tax';
const WALLETCONNECT_APP_DESCRIPTION =
  'VeChain tax analysis and Smart Scan for wallets.';

/** Origin-Text fürs Verbindungs-Zertifikat (DApp Kit Default, s. @vechain/dapp-kit). */
const connectionCertificateOrigin =
  typeof window !== 'undefined' ? window.location.origin : appUrl;

const WC_METADATA_VERSION_KEY = 'v3tax_wc_metadata_version';

if (typeof window !== 'undefined') {
  try {
    const currentVersion = window.localStorage.getItem(WC_METADATA_VERSION_KEY);

    if (currentVersion !== WC_METADATA_VERSION) {

      resetSignClient();

      Object.keys(window.localStorage).forEach((key) => {
        if (
          key.startsWith('wc@') ||
          key.startsWith('wc_') ||
          key.includes('walletconnect')
        ) {
          window.localStorage.removeItem(key);
        }
        /* DApp Kit 2.x (usePersistence): eigene Keys — sonst bleiben alte Verbindung/Metadaten
           für Embedded-Mobile hängen, während QR-Flow den frischen SignClient nutzt. */
        if (key.startsWith('dappkit@vechain')) {
          window.localStorage.removeItem(key);
        }
      });

      window.localStorage.setItem(WC_METADATA_VERSION_KEY, WC_METADATA_VERSION);
          }
  } catch (err) {
      }
}


const dappKitConfig = {
  // DApp Kit 2.x: Option heißt `node` (kein nodeUrl/genesis am Provider)
  node: vechainNode,
  usePersistence: true,
  requireCertificate: dappKitRequireCertificate,
  /**
   * Embedded VeWorld / thor_signCertificate: VeWorld zeigt oft NICHT WC-metadata.icons,
   * sondern leitet das Icon aus Domain/Favicon ab. `options.link` setzt explizit dieselbe
   * URL wie WalletConnect — gleiches Logo wie beim QR-Flow über walletconnectClient.js.
   */
  connectionCertificate: {
    message: {
      purpose: 'identification',
      payload: {
        type: 'text',
        content: `The following dApp would like to see your public address: ${connectionCertificateOrigin}`,
      },
    },
    options: {
      link: WALLETCONNECT_ICON_URL,
    },
  },
  ...(dappKitVeWorldOnly ? { allowedWallets: ['veworld'] } : {}),
  walletConnectOptions: {
    projectId: import.meta.env.VITE_WC_PROJECT_ID || '60635421b58cacf2ec5083593e40ffcf',
    metadata: {
      name: WALLETCONNECT_APP_NAME,
      description: WALLETCONNECT_APP_DESCRIPTION,
      url: appUrl,
      icons: [WALLETCONNECT_ICON_URL],
      redirect: {
        universal: dappKitMetadataUrl
      }
    },
    relayUrl: 'wss://relay.walletconnect.com',
    maxLoggingLevel: 2,
  }
};

if (typeof window !== "undefined") {
  window.__V3TAX_DAPPKIT_METADATA__ = {
    source: "main.jsx/DAppKitProvider",
    wcMetadataVersion: WC_METADATA_VERSION,
    requireCertificate: dappKitRequireCertificate,
    allowedWallets: dappKitVeWorldOnly ? ["veworld"] : "default",
    connectionCertificate: dappKitConfig.connectionCertificate,
    metadata: dappKitConfig.walletConnectOptions?.metadata,
  };

  window.__V3TAX_DEBUG_BRANDING__ = async () => {
    const urls = [
      "/v3tax-veworld-icon-16-20260514.png",
      "/v3tax-veworld-icon-32-20260514.png",
      "/v3tax-veworld-icon-180-20260514.png",
      "/v3tax-veworld-icon-192-20260514.png",
      "/v3tax-veworld-icon-512-20260514.png",
      "/v3tax-veworld-maskable-512-20260514.png",
      "/v3tax-veworld-icon-20260514.ico",
      "/manifest.webmanifest?v=20260514-veworld-splash-v1",
      "/favicon.ico",
      "/favicon-16x16.png",
      "/favicon-32x32.png",
      "/apple-touch-icon.png",
      "/android-chrome-192x192.png",
      "/android-chrome-512x512.png",
      "/logo-mobile-v3.png",
      "/logo.png",
      "/logo copy.png"
    ];

    const checks = await Promise.all(
      urls.map(async (path) => {
        const url = new URL(path, window.location.origin).toString();
        try {
          const res = await fetch(`${url}${url.includes("?") ? "&" : "?"}debug=${Date.now()}`, {
            cache: "no-store"
          });
          const blob = await res.blob();
          return {
            path,
            url,
            ok: res.ok,
            status: res.status,
            contentType: res.headers.get("content-type"),
            cacheControl: res.headers.get("cache-control"),
            bytes: blob.size
          };
        } catch (error) {
          return {
            path,
            url,
            ok: false,
            error: error?.message || String(error)
          };
        }
      })
    );

    return {
      href: window.location.href,
      title: document.title,
      dappKit: window.__V3TAX_DAPPKIT_METADATA__,
      links: [...document.querySelectorAll('link[rel*="icon"], link[rel="manifest"]')].map((el) => ({
        rel: el.getAttribute("rel"),
        href: el.href,
        sizes: el.getAttribute("sizes"),
        type: el.getAttribute("type")
      })),
      meta: [...document.querySelectorAll('meta[property^="og:"], meta[name^="twitter:"], meta[name="application-name"], meta[name="apple-mobile-web-app-title"]')].map((el) => ({
        key: el.getAttribute("property") || el.getAttribute("name"),
        content: el.getAttribute("content")
      })),
      checks
    };
  };

  window.__V3TAX_FIND_OLD_LOGOS__ = () => {
    const nodes = [...document.querySelectorAll("img, source, link, meta")];
    return nodes
      .map((el) => ({
        tag: el.tagName,
        src: el.getAttribute("src"),
        href: el.getAttribute("href"),
        content: el.getAttribute("content"),
        rel: el.getAttribute("rel"),
        property: el.getAttribute("property"),
        name: el.getAttribute("name"),
        className: el.getAttribute("class"),
        alt: el.getAttribute("alt"),
      }))
      .filter((x) => {
        const s = JSON.stringify(x).toLowerCase();
        return (
          s.includes("logo copy") ||
          s.includes("/logo.png") ||
          s.includes("header.png") ||
          s.includes("android-chrome") ||
          s.includes("favicon")
        );
      });
  };
}


createRoot(document.getElementById('root')).render(
  <StrictMode>
    <DAppKitProvider {...dappKitConfig}>
      {/* 🚀 Test: Minimaler Wrapper ohne komplexe Provider */}
      <BrowserRouter>
        <WalletProvider>
          <LanguageProvider>
            <AuthProvider>
              <Routes>
                <Route path="/" element={<AppShell />} />
                <Route path="/analyze/:datasetId?" element={<ProtectedRoute><AnalysisApp /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><AppShell /></ProtectedRoute>} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/payment-cancelled" element={<PaymentCancelled />} />
                <Route path="/preise" element={<AppShell />} /> {/* Alias für Marketing-Links */}
              </Routes>
            </AuthProvider>
          </LanguageProvider>
        </WalletProvider>
      </BrowserRouter>
    </DAppKitProvider>
  </StrictMode>
);

